// frontend/src/utils/pdfStorage.ts

export interface SavedPdfReport {
    id: string;
    name: string;
    date: string;
    timestamp: number;
    url: string; // Base64 data URL or remote HTTP URL
    projectDescription?: string;
    cargoSummary?: string;
}

const STORAGE_KEY = 'saved_lashing_pdf_reports';
const MAX_REPORTS = 5;

export const getSavedPdfReports = (): SavedPdfReport[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed: SavedPdfReport[] = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.sort((a, b) => b.timestamp - a.timestamp) : [];
    } catch (e) {
        console.error('Failed to parse saved PDF reports:', e);
        return [];
    }
};

export const savePdfReport = (report: Omit<SavedPdfReport, 'timestamp'>): SavedPdfReport => {
    const reports = getSavedPdfReports();
    const newReport: SavedPdfReport = {
        ...report,
        timestamp: Date.now()
    };

    // Filter out duplicate if same id exists
    const updated = [newReport, ...reports.filter(r => r.id !== newReport.id)].slice(0, MAX_REPORTS);
    
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
        console.warn('LocalStorage quota exceeded when saving PDF report, trimming oldest items:', e);
        // If quota exceeded (e.g. large base64), trim to last 2 items to protect other local data
        const trimmed = updated.slice(0, 2);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
        } catch (innerErr) {
            console.error('Could not save PDF report to localStorage:', innerErr);
        }
    }

    return newReport;
};

export const deletePdfReport = (id: string): SavedPdfReport[] => {
    const reports = getSavedPdfReports();
    const updated = reports.filter(r => r.id !== id);
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
        console.error('Failed to update PDF reports in localStorage after deletion:', e);
    }
    return updated;
};

export const clearAllPdfReports = (): void => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        console.error('Failed to clear PDF reports:', e);
    }
};
